// Economy UI components: Betting panel, Dust display, Shop modal, Payout animations

import { MultiplayerClient } from '../multiplayer/MultiplayerClient';
import { DustManager } from './DustManager';
import { BetPoolState, BetResult, PlayerRole, ShopItem, DustTransactionType } from '../multiplayer/protocol';

export class EconomyUI {
  private container: HTMLElement;
  private client: MultiplayerClient;
  private dustManager: DustManager;

  // UI elements
  private dustDisplay: HTMLElement | null = null;
  private bettingPanel: HTMLElement | null = null;
  private shopModal: HTMLElement | null = null;

  // State
  private currentPool: BetPoolState | null = null;
  private hasBet: boolean = false;

  constructor(container: HTMLElement, client: MultiplayerClient, dustManager: DustManager) {
    this.container = container;
    this.client = client;
    this.dustManager = dustManager;

    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    this.client.on('dustBalance', ({ dust }) => {
      this.dustManager.setBalance(dust);
      this.updateDustDisplay();
    });

    this.client.on('dustEarned', ({ amount, reason, newBalance }) => {
      this.dustManager.recordTransaction(reason, amount, newBalance);
      this.updateDustDisplay();
      this.showDustEarnedToast(amount, reason);
    });

    this.client.on('betPlaced', ({ pool }) => {
      this.currentPool = pool;
      this.updateBettingPanel();
    });

    this.client.on('betLocked', ({ pool }) => {
      this.currentPool = pool;
      this.updateBettingPanel();
    });

    this.client.on('betResult', ({ result }) => {
      this.showBetResultAnimation(result);
      this.hasBet = false;
    });

    this.dustManager.onChange(() => {
      this.updateDustDisplay();
    });
  }

  // ---- Dust Display (shown on menu and in-game) ----

  createDustDisplay(): void {
    if (this.dustDisplay) return;

    this.dustDisplay = document.createElement('div');
    this.dustDisplay.className = 'dust-display';
    this.dustDisplay.innerHTML = `
      <span class="dust-icon">&#x2728;</span>
      <span class="dust-amount" id="dust-amount">${this.dustManager.getBalance()}</span>
      <span class="dust-label">Arcane Dust</span>
    `;
    this.container.appendChild(this.dustDisplay);
  }

  removeDustDisplay(): void {
    if (this.dustDisplay) {
      this.dustDisplay.remove();
      this.dustDisplay = null;
    }
  }

  private updateDustDisplay(): void {
    const amountEl = this.dustDisplay?.querySelector('#dust-amount');
    if (amountEl) {
      const target = this.dustManager.getBalance();
      this.animateCounter(amountEl as HTMLElement, target);
    }
  }

  // ---- Betting Panel (for spectators) ----

  showBettingPanel(player1Name: string, player2Name: string): void {
    if (this.bettingPanel) return;
    this.hasBet = false;

    this.bettingPanel = document.createElement('div');
    this.bettingPanel.className = 'betting-panel';
    this.bettingPanel.innerHTML = `
      <div class="betting-header">Place Your Bet</div>
      <div class="betting-players">
        <div class="betting-player" data-player="player1">
          <div class="betting-player-name">${this.escapeHtml(player1Name || 'Player 1')}</div>
          <div class="betting-pool-amount" id="bet-pool-p1">0 dust</div>
        </div>
        <div class="betting-vs">VS</div>
        <div class="betting-player" data-player="player2">
          <div class="betting-player-name">${this.escapeHtml(player2Name || 'Player 2')}</div>
          <div class="betting-pool-amount" id="bet-pool-p2">0 dust</div>
        </div>
      </div>
      <div class="betting-amounts" id="betting-amounts">
        <button class="bet-amount-btn" data-amount="10">10</button>
        <button class="bet-amount-btn" data-amount="25">25</button>
        <button class="bet-amount-btn" data-amount="50">50</button>
        <button class="bet-amount-btn" data-amount="100">100</button>
      </div>
      <div class="betting-selected" id="betting-selected"></div>
      <div class="betting-status" id="betting-status">Select a player, then a bet amount</div>
      <div class="betting-balance">Your Dust: <span id="bet-balance">${this.dustManager.getBalance()}</span></div>
    `;
    this.container.appendChild(this.bettingPanel);

    // Player selection
    let selectedPlayer: PlayerRole | null = null;
    const playerEls = this.bettingPanel.querySelectorAll('.betting-player');
    playerEls.forEach(el => {
      el.addEventListener('click', () => {
        if (this.hasBet || this.currentPool?.locked) return;
        playerEls.forEach(p => p.classList.remove('selected'));
        el.classList.add('selected');
        selectedPlayer = el.getAttribute('data-player') as PlayerRole;
        this.updateBetStatus(selectedPlayer);
      });
    });

    // Bet amounts
    const amountBtns = this.bettingPanel.querySelectorAll('.bet-amount-btn');
    amountBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        if (this.hasBet || this.currentPool?.locked) return;
        if (!selectedPlayer) {
          this.setBetStatus('Select a player first!');
          return;
        }
        const amount = parseInt(btn.getAttribute('data-amount') || '0', 10);
        if (amount > this.dustManager.getBalance()) {
          this.setBetStatus('Not enough Arcane Dust!');
          return;
        }
        this.client.placeBet(amount, selectedPlayer);
        this.hasBet = true;
        this.setBetStatus(`Bet ${amount} dust on ${selectedPlayer === 'player1' ? player1Name : player2Name}!`);

        // Disable UI
        amountBtns.forEach(b => (b as HTMLElement).classList.add('disabled'));
        playerEls.forEach(p => (p as HTMLElement).classList.add('locked'));
      });
    });
  }

  hideBettingPanel(): void {
    if (this.bettingPanel) {
      this.bettingPanel.remove();
      this.bettingPanel = null;
    }
  }

  private updateBettingPanel(): void {
    if (!this.bettingPanel || !this.currentPool) return;

    const p1Pool = this.bettingPanel.querySelector('#bet-pool-p1');
    const p2Pool = this.bettingPanel.querySelector('#bet-pool-p2');
    const balanceEl = this.bettingPanel.querySelector('#bet-balance');

    if (p1Pool) p1Pool.textContent = `${this.currentPool.player1Total} dust`;
    if (p2Pool) p2Pool.textContent = `${this.currentPool.player2Total} dust`;
    if (balanceEl) balanceEl.textContent = String(this.dustManager.getBalance());

    if (this.currentPool.locked) {
      this.setBetStatus('Bets are locked - match starting!');
      const amountBtns = this.bettingPanel.querySelectorAll('.bet-amount-btn');
      amountBtns.forEach(b => (b as HTMLElement).classList.add('disabled'));
      const playerEls = this.bettingPanel.querySelectorAll('.betting-player');
      playerEls.forEach(p => (p as HTMLElement).classList.add('locked'));
    }

    // Show odds
    const total = this.currentPool.player1Total + this.currentPool.player2Total;
    if (total > 0) {
      const p1Odds = this.currentPool.player1Total > 0
        ? (total / this.currentPool.player1Total).toFixed(1)
        : '-';
      const p2Odds = this.currentPool.player2Total > 0
        ? (total / this.currentPool.player2Total).toFixed(1)
        : '-';
      const p1El = this.bettingPanel.querySelector('[data-player="player1"] .betting-pool-amount');
      const p2El = this.bettingPanel.querySelector('[data-player="player2"] .betting-pool-amount');
      if (p1El) p1El.textContent = `${this.currentPool.player1Total} dust (${p1Odds}x)`;
      if (p2El) p2El.textContent = `${this.currentPool.player2Total} dust (${p2Odds}x)`;
    }
  }

  private updateBetStatus(selectedPlayer: PlayerRole): void {
    this.setBetStatus(`Betting on ${selectedPlayer === 'player1' ? 'Player 1' : 'Player 2'} - choose amount`);
  }

  private setBetStatus(message: string): void {
    const statusEl = this.bettingPanel?.querySelector('#betting-status');
    if (statusEl) statusEl.textContent = message;
  }

  // ---- Bet Result Animation ----

  private showBetResultAnimation(result: BetResult): void {
    const toast = document.createElement('div');
    toast.className = `bet-result-toast ${result.won ? 'bet-win' : 'bet-loss'}`;

    if (result.won) {
      toast.innerHTML = `
        <div class="bet-result-icon">&#x2728;</div>
        <div class="bet-result-text">
          <div class="bet-result-title">You Won!</div>
          <div class="bet-result-amount">+${result.profit} Arcane Dust</div>
        </div>
        <div class="bet-result-sparkles"></div>
      `;
    } else if (result.profit === 0) {
      // Refund
      toast.innerHTML = `
        <div class="bet-result-icon">&#x21A9;</div>
        <div class="bet-result-text">
          <div class="bet-result-title">Bet Refunded</div>
          <div class="bet-result-amount">${result.payout} dust returned</div>
        </div>
      `;
      toast.className = 'bet-result-toast bet-refund';
    } else {
      toast.innerHTML = `
        <div class="bet-result-icon">&#x1F614;</div>
        <div class="bet-result-text">
          <div class="bet-result-title">Better Luck Next Time</div>
          <div class="bet-result-amount">${result.profit} Arcane Dust</div>
        </div>
      `;
    }

    this.container.appendChild(toast);

    // Auto-dismiss after 4 seconds
    setTimeout(() => {
      toast.classList.add('bet-result-exit');
      setTimeout(() => toast.remove(), 500);
    }, 4000);
  }

  // ---- Dust Earned Toast ----

  private showDustEarnedToast(amount: number, reason: DustTransactionType): void {
    const labels: Record<DustTransactionType, string> = {
      match_win: 'Match Victory',
      match_loss: 'Match Participation',
      achievement: 'Achievement Unlocked',
      tournament_win: 'Tournament Victory',
      daily_login: 'Daily Login Bonus',
      bet_win: 'Bet Won',
      bet_loss: 'Bet Lost',
      shop_purchase: 'Shop Purchase',
      bet_refund: 'Bet Refunded',
    };

    // Don't show toast for negative amounts (purchases)
    if (amount <= 0 && reason !== 'bet_refund') return;

    const toast = document.createElement('div');
    toast.className = 'dust-earned-toast';
    toast.innerHTML = `
      <span class="dust-earned-icon">&#x2728;</span>
      <span class="dust-earned-text">+${amount} Dust</span>
      <span class="dust-earned-reason">${labels[reason] || reason}</span>
    `;
    this.container.appendChild(toast);

    setTimeout(() => {
      toast.classList.add('dust-toast-exit');
      setTimeout(() => toast.remove(), 500);
    }, 3000);
  }

  // ---- Shop Modal ----

  showShop(items: ShopItem[], ownedIds: Set<string>): void {
    if (this.shopModal) this.shopModal.remove();

    this.shopModal = document.createElement('div');
    this.shopModal.className = 'menu-screen';
    this.shopModal.style.background = 'rgba(0, 0, 0, 0.95)';
    this.shopModal.setAttribute('role', 'dialog');
    this.shopModal.setAttribute('aria-label', 'Arcane Dust Shop');

    const renderItems = () => {
      return items.map(item => {
        const owned = ownedIds.has(item.cosmeticId);
        const canAfford = this.dustManager.getBalance() >= item.price;

        return `
          <div class="shop-item ${owned ? 'owned' : ''} ${!canAfford && !owned ? 'cant-afford' : ''}">
            <div class="shop-item-type">${this.getTypeLabel(item.type)}</div>
            <div class="shop-item-name">${this.escapeHtml(item.name)}</div>
            <div class="shop-item-desc">${this.escapeHtml(item.description)}</div>
            <div class="shop-item-price">
              ${owned
                ? '<span class="shop-owned-badge">Owned</span>'
                : `<span class="shop-price-amount">&#x2728; ${item.price}</span>
                   <button class="shop-buy-btn ${canAfford ? '' : 'disabled'}"
                           data-cosmetic-id="${item.cosmeticId}"
                           ${canAfford ? '' : 'disabled'}>
                     ${canAfford ? 'Buy' : 'Need more dust'}
                   </button>`
              }
            </div>
          </div>
        `;
      }).join('');
    };

    this.shopModal.innerHTML = `
      <h2 class="game-title" style="font-size: 2rem;">Arcane Dust Shop</h2>
      <div class="ornament"></div>
      <div class="shop-balance">
        <span class="dust-icon">&#x2728;</span>
        <span class="shop-balance-amount" id="shop-balance">${this.dustManager.getBalance()}</span>
        <span class="dust-label">Arcane Dust</span>
      </div>
      <div class="shop-grid" id="shop-grid">${renderItems()}</div>
      <button class="menu-button" id="btn-close-shop" style="margin-top: 1.5rem;">Back</button>
    `;
    this.container.appendChild(this.shopModal);

    // Buy button listeners
    const attachBuyListeners = () => {
      this.shopModal?.querySelectorAll('.shop-buy-btn:not(.disabled)').forEach(btn => {
        btn.addEventListener('click', () => {
          const cosmeticId = btn.getAttribute('data-cosmetic-id');
          if (cosmeticId) {
            this.client.purchaseCosmetic(cosmeticId);
            // Optimistically update
            (btn as HTMLElement).textContent = 'Purchasing...';
            (btn as HTMLElement).classList.add('disabled');
          }
        });
      });
    };
    attachBuyListeners();

    // Listen for purchase results
    const purchaseHandler = this.client.on('purchaseResult', ({ success, cosmeticId, error }) => {
      if (success) {
        ownedIds.add(cosmeticId);
      }
      // Re-render the shop grid
      const grid = this.shopModal?.querySelector('#shop-grid');
      if (grid) {
        grid.innerHTML = renderItems();
        attachBuyListeners();
      }
      const balanceEl = this.shopModal?.querySelector('#shop-balance');
      if (balanceEl) balanceEl.textContent = String(this.dustManager.getBalance());

      if (!success && error) {
        this.showShopError(error);
      }
    });

    // Balance updates
    const balanceHandler = this.dustManager.onChange((balance) => {
      const balanceEl = this.shopModal?.querySelector('#shop-balance');
      if (balanceEl) balanceEl.textContent = String(balance);
    });

    // Close handler
    this.shopModal.querySelector('#btn-close-shop')?.addEventListener('click', () => {
      purchaseHandler();
      balanceHandler();
      this.shopModal?.remove();
      this.shopModal = null;
    });
  }

  private showShopError(message: string): void {
    const toast = document.createElement('div');
    toast.className = 'toast';
    toast.style.borderColor = '#ff6b6b';
    toast.style.color = '#ff6b6b';
    toast.textContent = message;
    this.container.appendChild(toast);

    setTimeout(() => {
      toast.classList.add('fade-out');
      setTimeout(() => toast.remove(), 500);
    }, 3000);
  }

  private getTypeLabel(type: string): string {
    const labels: Record<string, string> = {
      spell_skin: 'Spell Skin',
      wizard_border: 'Wizard Border',
      victory_animation: 'Victory Animation',
      arena_theme: 'Arena Theme',
    };
    return labels[type] || type;
  }

  // ---- Utilities ----

  private animateCounter(el: HTMLElement, target: number): void {
    const current = parseInt(el.textContent || '0', 10);
    if (current === target) return;

    const diff = target - current;
    const steps = Math.min(Math.abs(diff), 20);
    const stepSize = diff / steps;
    let step = 0;

    const interval = setInterval(() => {
      step++;
      const value = step >= steps ? target : Math.round(current + stepSize * step);
      el.textContent = String(value);
      if (step >= steps) {
        clearInterval(interval);
        el.textContent = String(target);
      }
    }, 50);
  }

  private escapeHtml(str: string): string {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  destroy(): void {
    this.removeDustDisplay();
    this.hideBettingPanel();
    if (this.shopModal) {
      this.shopModal.remove();
      this.shopModal = null;
    }
  }
}
